import { Glue42Core } from "@glue42/core";
import { Glue42Office } from "../../glue4office";
import { ColumnsAsJson, DataAsJson, ShowGridInvocationResult, ShowGridParams, ValidationObject } from "./types";

const shortid = require("shortid");

function getColumnsAsJson(columns: Glue42Office.Excel.ColumnConfig[]): ColumnsAsJson[] {
    const modifiedColumns = columns.map((col: Glue42Office.Excel.ColumnConfig) => {
        const column: ColumnsAsJson = {
            name: col.fieldName || "",
            text: col.header || col.fieldName,
            foregroundColor: col.foregroundColor || "",
            backgroundColor: col.backgroundColor || "",
            width: isNumber(col.width) ? col.width : null,
        };

        if (col.validation) {
            column.validation = {
                type: ("xlValidate" + col.validation.type) as any,
                alert: ("xlValidAlert" + col.validation.alert) as any,
                list: col.validation.list || null,
            };
        }

        return column;
    });

    return modifiedColumns;
}

// https://stackoverflow.com/a/36643588/1527706
function formatDate(date: Date) {
    const timezoneOffsetInHours = -(date.getTimezoneOffset() / 60); // UTC minus local time
    const sign = timezoneOffsetInHours >= 0 ? "+" : "-";
    const leadingZero = (Math.abs(timezoneOffsetInHours) < 10) ? "0" : "";

    const correctedDate = new Date(date.getFullYear(), date.getMonth(),
        date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(),
        date.getMilliseconds());
    correctedDate.setHours(date.getHours() + timezoneOffsetInHours);
    const iso = correctedDate.toISOString().replace("Z", "");

    let suffix = (date.getTimezoneOffset() % 60) + "";
    if (suffix.length < 2) {
        suffix = "0" + suffix;
    }
    return iso + sign + leadingZero + Math.abs(timezoneOffsetInHours).toString() + ":" + suffix;
}

function getDataAsJson(columns: Glue42Office.Excel.ColumnConfig[], data: object[]): DataAsJson[] {
    const modifiedData = data.map((row: any) => {
        const dataList: any = [];
        columns
            .map((col: Glue42Office.Excel.ColumnConfig) => ({ forceText: col.forceText, preserveEmptyString: col.preserveEmptyString, name: col.fieldName }))
            .forEach(({ forceText, preserveEmptyString, name }: { forceText: boolean, preserveEmptyString: boolean, name: string }) => {
                const rowValue = row[name] || row[name.toLowerCase()]
                    || row[Object.keys(row).find((key) => key.toLowerCase() === name.toLowerCase())];

                let item;
                if (forceText && isFinite(rowValue)) {
                    item = "'" + rowValue;
                } else if (preserveEmptyString && (rowValue === null || rowValue === undefined || rowValue === "")) {
                    item = "'";
                } else if (typeof rowValue === "number" && isFinite(rowValue)) {
                    item = rowValue;
                } else if (rowValue instanceof Date) {
                    item = formatDate(rowValue);
                } else {
                    item = rowValue || "";
                }
                dataList.push(item);
            });

        return { data: dataList };
    });

    return modifiedData;
}

function getShowGridParams(options: Glue42Office.Excel.OpenSheetOptions): Partial<ShowGridParams> {
    // This is object, which is used to convert options from friendly name to the method"s params and sets the default value
    const methodOptions: any = {
        clearGrid: {
            default: true,
            friendlyName: undefined,
        },
        workbook: {
            default: shortid.generate(),
            friendlyName: undefined,
        },
        dataWorksheet: {
            default: shortid.generate(),
            friendlyName: "worksheet",
        },
        templateWorkbook: undefined,
        viewWorksheet: {
            friendlyName: "templateWorksheet",
        },
        inhibitLocalSave: undefined,
        glueMethod: {
            default: "ValidateShowGrid",
            friendlyName: undefined,
        },
        buttonText: undefined,
        buttonRange: {
            default: "A1",
            friendlyName: undefined,
        },
        topLeft: {
            default: "A1",
            friendlyName: undefined,
        },
        triggers: {
            default: ["save"],
            friendlyName: "updateTrigger",
        },
        dataRangeName: undefined,
        window: undefined,
        chunkSize: {
            default: 1000,
            friendlyName: undefined,
        },
        disableErrorViewer: undefined,
        autostart: undefined,
        displayName: undefined,
        timeoutMs: undefined,
    };

    const updatedOptions: Partial<ShowGridParams> = {};
    options = options || {};

    Object.keys(methodOptions).forEach((key: string) => {
        const value = methodOptions[key];
        const friendlyName = value ? value.friendlyName : undefined;
        const defaultValue = value ? value.default : undefined;

        if (friendlyName) {
            updatedOptions[key] = (options as any)[friendlyName];
        } else {
            updatedOptions[key] = (options as any)[key];
        }

        if (value && updatedOptions[key] === undefined) {
            updatedOptions[key] = defaultValue;
        }
    });

    for (const key in options) {
        if ((!(key in methodOptions) ||
            (methodOptions[key] &&
                methodOptions[key].friendlyName &&
                methodOptions[key].friendlyName !== key)) &&
            !Object.keys(methodOptions).some((x) => methodOptions[x] && methodOptions[x].friendlyName === key)) {
            updatedOptions[key] = options[key];
        }
    }

    return updatedOptions;
}

function isNumber(n: any) {
    return !isNaN(parseFloat(n)) && isFinite(n);
}

export function parseAgmResult(r: Glue42Core.AGM.InvocationResult<ShowGridInvocationResult>): boolean {
    let resp: boolean;
    try {
        resp = JSON.parse(r.returned.result).success;
    } catch (error) {
        resp = false;
    }
    return resp;
}

export function convertParams(
    columns: Glue42Office.Excel.ColumnConfig[],
    data: object[],
    options: Glue42Office.Excel.OpenSheetOptions,
    cookie?: string): { params: ShowGridParams, agmOptions: Glue42Core.AGM.InvokeOptions } {
    const params: ShowGridParams = {
        columnsAsJSON: JSON.stringify(getColumnsAsJson(columns)),
        dataAsJSON: JSON.stringify(getDataAsJson(columns, data)),
        cookie: cookie ? cookie : shortid.generate(),
        response: (options && options.response === "image") ? "image" : "row",
    };
    const agmOptions = options && { methodResponseTimeoutMs: options.timeoutMs };

    // the point of this call is to populate defaults and map friendly names to agm names
    const mappedParams: Partial<ShowGridParams> = getShowGridParams(options);
    // Workaround for Object.assign(params, op);
    const combinedParams = { ...params, ...mappedParams };

    // get rid of AGM options
    delete combinedParams.timeoutMs;

    return {
        params: combinedParams,
        agmOptions,
    };
}

export function flatten(arr: any[][]) {
    const flattenedArray = [];
    // tslint:disable-next-line:prefer-for-of
    for (let ii = 0; ii < arr.length; ++ii) {
        const current = arr[ii];
        flattenedArray.push.apply(flattenedArray, current);
    }
    return flattenedArray;
}

export function camelize(str: string) {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
        if (+match === 0) {
            return "";
        }
        // or if (/\s+/.test(match)) for white spaces
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
}

export const sheetIdSeparator = "_";

export function getSheetId(workbook: string, worksheet: string) {
    return workbook + sheetIdSeparator + worksheet;
}
